/**
 * Шаги выполнения работы (ПЛ-36): правило по виду работ, процент — по закрытым шагам. Шаг «акт» закрывается
 * подписанным актом освидетельствования по этой работе.
 */
import { type ActivitySteps, type ProgressRuleRow, STEP_CLOSERS, type StepCloser } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { toast } from '../../ui/feedback';
import { Select } from '../../ui/form';
import { scheduleKey } from './useSchedule';

const dd = (s: string) => s.slice(0, 10).split('-').reverse().join('.');

export function ActivityStepsBlock({
  projectId,
  activityId,
  workType,
  onPercent,
}: {
  projectId: string;
  activityId: string;
  workType: string | null;
  onPercent: (percent: number, hasSteps: boolean) => void;
}) {
  const qc = useQueryClient();
  const key = ['activity-steps', activityId];
  const q = useQuery({ queryKey: key, queryFn: () => api<ActivitySteps>(`/activities/${activityId}/steps`) });
  const rules = useQuery({
    queryKey: ['progress-rules'],
    queryFn: () => api<ProgressRuleRow[]>('/progress-rules'),
    enabled: Boolean(q.data?.canEdit),
  });
  const h = q.data;
  const cb = useRef(onPercent);
  cb.current = onPercent;
  useEffect(() => {
    if (h) cb.current(h.percent, h.steps.length > 0);
  }, [h]);
  const done = (r: ActivitySteps) => {
    qc.setQueryData(key, r);
    // График пересчитан: процент и остаток работы изменились
    void qc.invalidateQueries({ queryKey: scheduleKey(projectId) });
  };
  const setRule = useMutation({
    mutationFn: (ruleId: string | null) =>
      api<ActivitySteps>(`/activities/${activityId}/steps`, { method: 'PUT', body: { ruleId } }),
    onSuccess: done,
    onError: (e) => toast(e.message, 'danger'),
  });
  const mark = useMutation({
    mutationFn: (x: { id: string; done: boolean }) =>
      api<ActivitySteps>(`/activity-steps/${x.id}`, { method: 'PATCH', body: { done: x.done } }),
    onSuccess: done,
    onError: (e) => toast(e.message, 'danger'),
  });
  if (!h || (!h.steps.length && !h.canEdit)) return null;
  // Правила этого вида работ и общие; назначенное правило — всегда в списке
  const own = (rules.data ?? []).filter(
    (r) => r.id === h.ruleId || !workType || !r.workType || r.workType === workType,
  );
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[14px] font-semibold">
        Шаги выполнения{h.steps.length ? ` · ${String(h.percent).replace('.', ',')} %` : ''}
      </h3>
      {h.canEdit ? (
        <div className="flex flex-wrap items-center gap-2 text-[13px]">
          <Select
            value={h.ruleId ?? ''}
            onChange={(e) => setRule.mutate(e.target.value || null)}
            aria-label="Правило начисления выполнения"
            className="max-w-full max-md:h-11"
          >
            <option value="">
              {h.steps.length && !h.ruleId
                ? 'Правило удалено — шаги остались'
                : 'Без правила — процент вручную'}
            </option>
            {own.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.workType ? ` (${r.workType})` : ''}
              </option>
            ))}
          </Select>
          {h.suggested && !h.steps.length ? (
            <Button size="sm" onClick={() => setRule.mutate(h.suggested!.id)} className="max-md:h-11">
              Применить «{h.suggested.name}» по виду работ
            </Button>
          ) : null}
          {!rules.data?.length && rules.isSuccess ? (
            <span className="text-ink-2">Правил пока нет — их заводит администратор справочников.</span>
          ) : null}
        </div>
      ) : null}
      {h.steps.length ? (
        <ul className="divide-y divide-line text-[14px]">
          {h.steps.map((s) => {
            const auto = s.closeBy === 'act';
            const can = h.canEdit && (!auto || Boolean(s.doneAt));
            return (
              <li key={s.id} className="flex flex-wrap items-center justify-between gap-2 py-1.5">
                <label className={clsx('flex min-h-11 items-center gap-2 md:min-h-0', !can && 'opacity-70')}>
                  <input
                    type="checkbox"
                    checked={Boolean(s.doneAt)}
                    disabled={!can || mark.isPending}
                    onChange={(e) => mark.mutate({ id: s.id, done: e.target.checked })}
                  />
                  <span>
                    {s.name} <span className="text-ink-2">· {String(s.weight).replace('.', ',')} %</span>
                  </span>
                </label>
                <span className="text-[12px] text-ink-2">
                  {s.doneAt ? (
                    <>
                      закрыт {dd(s.doneAt)}
                      {s.doneByName ? `, ${s.doneByName}` : ''}
                      {s.sourceType === 'inspection' && s.sourceId ? (
                        <>
                          {' '}
                          ·{' '}
                          <Link
                            to="/supervision/inspections/$inspectionId"
                            params={{ inspectionId: s.sourceId }}
                            className="inline-flex min-h-11 items-center hover:underline md:min-h-0"
                          >
                            акт
                          </Link>
                        </>
                      ) : s.closeBy !== 'manual' ? (
                        ' · вручную'
                      ) : null}
                    </>
                  ) : auto ? (
                    <Link
                      to="/supervision"
                      search={{ project: projectId, tab: 'inspections' } as never}
                      className="inline-flex min-h-11 items-center text-brand hover:underline md:min-h-0"
                    >
                      Принять акт с выбором шага
                    </Link>
                  ) : s.evidence ? (
                    <span className="text-success">можно закрыть: {s.evidence.title}</span>
                  ) : (
                    `закрывает: ${STEP_CLOSERS[s.closeBy as StepCloser].toLowerCase()}`
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}
